import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { MdKeyboardArrowDown } from 'react-icons/md';

import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    // compact は選択系コンポーネント統合の互換のため (#3277)
    variant?: 'default' | 'compact';
  }
>(({ className, children, variant = 'default', ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'group flex h-xxl w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white',
      'disabled:cursor-not-allowed [&>span]:line-clamp-1',
      variant === 'compact'
        ? 'px-3 text-left text-sm data-[placeholder]:text-gray-500 data-[state=open]:border-2 data-[state=open]:border-primary'
        : 'px-4 text-body-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary disabled:opacity-50 data-[placeholder]:text-placeholder data-[state=open]:outline data-[state=open]:outline-2 data-[state=open]:outline-offset-[-2px] data-[state=open]:outline-primary',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <MdKeyboardArrowDown
        className={cn(
          'text-xl text-main transition-transform group-data-[state=open]:rotate-180',
          variant === 'compact' ? 'duration-100' : 'duration-200'
        )}
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    maxHeight?: string;
    footer?: React.ReactNode;
    // compact は選択系コンポーネント統合の互換のため (#3277)
    variant?: 'default' | 'compact';
  }
>(
  (
    {
      className,
      children,
      position = 'popper',
      maxHeight,
      footer,
      variant = 'default',
      sideOffset,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-[9999] w-full min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-sm bg-white text-main',
          'origin-[--radix-select-content-transform-origin] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          variant === 'compact'
            ? 'py-1 shadow-[0px_0px_12px_0px_#00000033]'
            : 'border border-border shadow-layer3',
          className
        )}
        position={position}
        sideOffset={sideOffset}
        style={{
          maxHeight: maxHeight || 'var(--radix-select-content-available-height)',
          ...props.style,
        }}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'p-0',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {footer}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-body-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    // compact は選択系コンポーネント統合の互換のため (#3277)
    variant?: 'default' | 'compact';
  }
>(({ className, children, variant = 'default', ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      variant === 'compact'
        ? 'px-3 py-2 text-sm hover:bg-[#F6F6F6] data-[state=checked]:font-medium'
        : 'min-h-xxl cursor-pointer px-xs py-2 text-body-sm leading-relaxed hover:bg-gray focus:bg-gray focus:text-main data-[state=checked]:bg-gray',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('mx-1 my-1 h-px bg-light-gray', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
